/**
 * useFloodPrevention — polls /api/flood/prevention every 5 min (server
 * TTL) and exposes a single CampusFloodPrevention snapshot or null while
 * loading / on error.
 *
 * Uses the same SWR-style caching as useFeed (localStorage roundtrip) so
 * the prevention panel renders instantly from disk on cold load.
 */

import { useEffect, useState } from "react";
import type { CampusFloodPrevention, NormalizedFeed } from "@chula/shared";

interface UseFloodPreventionResult {
  data: NormalizedFeed<CampusFloodPrevention> | null;
  error: string | null;
  isLoading: boolean;
}

const STORAGE_KEY = "chula:flood-prevention:path";
const POLL_MS = 5 * 60 * 1000;
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;

function readCached(): { snapshot: NormalizedFeed<CampusFloodPrevention>; savedAt: number } | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { snapshot: NormalizedFeed<CampusFloodPrevention>; savedAt: number };
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_CACHE_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCached(snapshot: NormalizedFeed<CampusFloodPrevention>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ snapshot, savedAt: Date.now() }));
  } catch {
    /* quota or private-mode — ignore */
  }
}

export function useFloodPrevention(apiBase: string): UseFloodPreventionResult {
  const [data, setData] = useState<NormalizedFeed<CampusFloodPrevention> | null>(() => {
    const cached = readCached();
    return cached?.snapshot ?? null;
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() => readCached() == null);

  useEffect(() => {
    let cancelled = false;
    let inflight: AbortController | null = null;

    const run = async () => {
      inflight?.abort();
      const ctrl = new AbortController();
      inflight = ctrl;
      try {
        const url = `${apiBase}/api/flood/prevention?_=${Date.now()}`;
        const res = await fetch(url, { signal: ctrl.signal, cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const json = (await res.json()) as NormalizedFeed<CampusFloodPrevention>;
        if (cancelled) return;
        if (!Array.isArray(json?.features) || !json?.meta) throw new Error("Invalid feed response");
        setData(json);
        writeCached(json);
        setError(null);
      } catch (err) {
        if (cancelled || (err as Error).name === "AbortError") return;
        setError((err as Error).message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    const id = setInterval(() => void run(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      inflight?.abort();
    };
  }, [apiBase]);

  return { data, error, isLoading };
}
